import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import { useMutation } from '@tanstack/react-query';

import { useAuth } from '../contexts/AuthenticationContext';
import { profileApi } from '../endpoints';

// Matches the backend cooldown in ProfileController::RESEND_VERIFICATION_COOLDOWN
// so the button re-enables right about when the server would accept another send.
const RESEND_COOLDOWN_SECONDS = 60;

// Pulls the human-readable message off an Axios error; 429 (cooldown) is a
// soft "warning" rather than a hard error.
function resendFeedback(error: unknown): { severity: 'warning' | 'error'; text: string } {
  const e = error as { response?: { status?: number; data?: { error?: string } } } | null;
  const text = e?.response?.data?.error ?? 'Could not resend the verification email. Please try again.';
  return { severity: e?.response?.status === 429 ? 'warning' : 'error', text };
}

// Shown when the API rejects sign-in with `code: "email_verification_required"`
// — the Auth0 token is valid but the address isn't verified yet. Without this,
// the app would sit on the loading spinner while every authenticated query 403s.
export default function VerifyEmailScreen() {
  const { user, login, logout } = useAuth();
  const [cooldown, setCooldown] = useState(0);

  const resend = useMutation({
    mutationFn: profileApi.resendVerification,
    onSuccess: () => setCooldown(RESEND_COOLDOWN_SECONDS),
  });

  // Tick the cooldown down to zero, one second at a time.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const feedback = resend.isSuccess
    ? { severity: 'success' as const, text: resend.data?.message ?? 'Verification email sent. Check your inbox.' }
    : resend.isError
      ? resendFeedback(resend.error)
      : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        px: 2,
        backgroundColor: 'background.default',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 460,
          width: '100%',
          textAlign: 'center',
          p: { xs: 3, sm: 5 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          component="img"
          src="/wz-icon-blue.png"
          alt="Wicklander-Zulawski"
          sx={{ height: 44, width: 44, objectFit: 'contain', mb: 3 }}
        />

        <MarkEmailUnreadOutlinedIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1.5 }} />

        <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
          Verify your email
        </Typography>

        <Typography sx={{ color: 'text.secondary', mb: 1 }}>
          We sent a verification link to
          {user?.email ? <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}> {user.email}</Box> : ' your email address'}
          . Click the link to confirm your address, then continue.
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
          Can&apos;t find it? Check your spam folder.
        </Typography>

        {feedback && (
          <Alert severity={feedback.severity} sx={{ mb: 3, textAlign: 'left' }}>
            {feedback.text}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Re-auth rather than a plain reload: a silent token refresh can hand
              back a cached token still flagged unverified. Going through Auth0
              again mints a fresh token reflecting the now-verified address. */}
          <Button variant="contained" color="secondary" size="large" onClick={() => login()}>
            I&apos;ve verified my email
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => resend.mutate()}
            disabled={resend.isPending || cooldown > 0}
          >
            {resend.isPending
              ? 'Sending…'
              : cooldown > 0
                ? `Resend verification email (${cooldown}s)`
                : 'Resend verification email'}
          </Button>
          <Button variant="text" color="inherit" onClick={() => logout()}>
            Sign out
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
